import { Emulator } from './emulator';
import { createCartridge } from './game/cartridge';
// input-agent imports (part of the marked input-agent section)
import { StandardController } from './hardware/input/standard-controller';
import { InputMux } from './hardware/input/input-mux';
import { KeyboardAdapter } from './hardware/input/keyboard';
import { GamepadAdapter } from './hardware/input/gamepad';
import { ApuAudioPlayer } from './audio/audio-player';

// Integration-owned browser entry point — see AGENTS.md.

async function fetchRomBytes(url: string): Promise<Uint8Array | null> {
    const response = await fetch(url);
    if (!response.ok) return null; // 404 page bytes are not a ROM
    return new Uint8Array(await response.arrayBuffer());
}

class NesEmulator {
    private emulator: Emulator | null = null;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private running: boolean = false;
    private animationFrameId: number | null = null;
    private audio: ApuAudioPlayer | null = null;
    private imageData: ImageData = new ImageData(256, 240);

    constructor(romBytes: Uint8Array | null, canvasId: string) {
        if (romBytes) {
            this.emulator = new Emulator(createCartridge(romBytes));
        }
        this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        this.ctx = this.canvas.getContext('2d')!;
        this.ctx.imageSmoothingEnabled = false; // Pixelated rendering
        if (!romBytes) {
            // Idle screen when no default ROM is bundled
            this.ctx.fillStyle = '#000';
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            this.ctx.fillStyle = '#888';
            this.ctx.font = '12px monospace';
            this.ctx.textAlign = 'center';
            this.ctx.fillText('Choose File to load a ROM (.nes)', this.canvas.width / 2, this.canvas.height / 2);
        }
        this.attachInput();
    }

    getEmulator(): Emulator | null {
        return this.emulator;
    }

    start() {
        if (!this.emulator) return; // no ROM loaded yet
        // Audio glue: create once, follow the current APU, retry resume on
        // every call (autoplay policies suspend contexts created pre-gesture).
        if (!this.audio) {
            this.audio = new ApuAudioPlayer(this.emulator.getNes().getApu());
        }
        this.audio.start();
        if (!this.running) {
            this.running = true;
            this.lastTimestamp = null;
            this.frameAccumulator = 0;
            this.runFrame();
        }
    }

    stop() {
        this.running = false;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        if (this.audio) {
            this.audio.stop();
        }
    }

    stepFrame() {
        if (!this.emulator) return;
        this.emulator.stepFrame();
        this.renderFrame();
    }

    loadNewRom(romBytes: Uint8Array) {
        this.stop();
        this.emulator = new Emulator(createCartridge(romBytes));
        this.attachInput();
        if (this.audio) {
            // Keep the running AudioContext; just follow the new APU instance.
            this.audio.setSource(this.emulator.getNes().getApu());
        }
        this.start(); // a ROM chosen by hand is a gesture — just play it
    }

    // NTSC NES runs at ~60.0988 fps. Pace emulation to wall-clock time so the
    // game speed (and scroll smoothness) is independent of the display's rAF
    // rate (60Hz, 120Hz ProMotion, etc.).
    private static readonly FRAME_MS = 1000 / 60.0988;
    private lastTimestamp: number | null = null;
    private frameAccumulator = 0;

    private runFrame(timestamp?: number) {
        if (!this.running || !this.emulator) return;
        if (timestamp === undefined || this.lastTimestamp === null) {
            // First tick (or manual call): run exactly one frame.
            this.emulator.stepFrame();
            this.frameAccumulator = 0;
        } else {
            // Cap catch-up debt so background-tab throttling can't spiral.
            this.frameAccumulator += Math.min(timestamp - this.lastTimestamp, 100);
            let steps = 0;
            while (this.frameAccumulator >= NesEmulator.FRAME_MS && steps < 3) {
                this.emulator.stepFrame();
                this.frameAccumulator -= NesEmulator.FRAME_MS;
                steps++;
            }
            if (steps === 3) this.frameAccumulator = 0; // drop backlog
        }
        this.lastTimestamp = timestamp ?? null;
        this.renderFrame();
        this.animationFrameId = requestAnimationFrame((t) => this.runFrame(t));
    }

    private renderFrame() {
        if (!this.emulator) return;
        const buffer = this.emulator.getNes().getPpu().getFrameBuffer();
        if (buffer) {
            this.imageData.data.set(buffer);
            this.ctx.putImageData(this.imageData, 0, 0);
        } else {
            this.ctx.fillStyle = 'black';
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
    }

    /* ===== BEGIN input-agent section (keyboard/gamepad wiring) =====
     * input-agent may replace the body of attachInput() and add imports from
     * src/hardware/input/**. Everything else in this file is integrator-owned.
     */
    private inputController: StandardController | null = null;
    private inputMux: InputMux | null = null;
    private keyboardAdapter: KeyboardAdapter | null = null;
    private gamepadAdapter: GamepadAdapter | null = null;

    public attachInput() {
        // One controller persists across ROM reloads; each new Nes instance
        // gets it re-registered on port 1 ($4016).
        if (!this.inputController) {
            this.inputController = new StandardController();
            this.inputMux = new InputMux(this.inputController);
            this.keyboardAdapter = new KeyboardAdapter(this.inputMux, window);
            this.gamepadAdapter = new GamepadAdapter(this.inputMux);
            const pollGamepad = () => {
                this.gamepadAdapter!.update();
                requestAnimationFrame(pollGamepad);
            };
            requestAnimationFrame(pollGamepad);
        }
        this.emulator?.getNes().setController(0, this.inputController);
    }
    /* ===== END input-agent section ===== */
}

async function main() {
    // Default ROM (game/smb.nes) is optional — it isn't committed to the repo
    // (commercial ROMs can't be distributed). If absent, the page loads clean
    // and the user picks a ROM via the file input.
    const romBytes = await fetchRomBytes('game/smb.nes');
    if (!romBytes) {
        console.info('No bundled default ROM (game/smb.nes) — use Choose File to load one.');
    }
    const emulator = new NesEmulator(romBytes, 'nes-screen');

    document.getElementById('start')!.addEventListener('click', () => emulator.start());
    document.getElementById('stop')!.addEventListener('click', () => emulator.stop());
    document.getElementById('step-frame')!.addEventListener('click', () => emulator.stepFrame());

    document.getElementById('rom-input')!.addEventListener('change', async (event) => {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const romBytes = new Uint8Array(await input.files[0].arrayBuffer());
            emulator.loadNewRom(romBytes);
        }
    });

    emulator.start();
}

main().catch(console.error);
